import kebabCase from 'lodash/kebabCase';

import { request } from '@core/api';
import YouTubeUrl from '@core/validators/YouTubeUrlValidator';
import BaseModel from '@core/model';

import MediaFormatEnum from '@constants/enums/media/format';
import MediaTypeEnum from '@constants/enums/media/type';

import MediaApi from '@packages/media/api';

import { attachHeaders } from '@utils/api';
import { getFileTypeIcon } from '@utils/image';
import { formatString } from '@utils/string';

import { toTipTap } from '@plugins/tiptap/utils';

import config from '@/config';

export class Media extends BaseModel {
    static name = 'Media';
    static entity = 'media';
    static Api = MediaApi;

    static fields() {
        return {
            id: this.attr(null),
            library_type: this.enum(MediaTypeEnum).nullable(),
            name: this.string(null).nullable(),
            description: this.attr(null).nullable(),
            alt_text: this.string(null).nullable(),
            download: this.boolean(true),
            format: this.enum(MediaFormatEnum),
            external_url: this.string(null).nullable(),
            created_at: this.string(null).nullable(),
            created_by_id: this.string(null).nullable(),
            created_by: this.attr(null).nullable(),

            file: this.attr(null).nullable(),
            file_id: this.string(null).nullable(),
            file_awaiting_upload: this.attr(null).nullable(),

            audit_file: this.attr(null).nullable(),
            audit_file_id: this.string(null).nullable(),
            audit_file_awaiting_upload: this.attr(null).nullable(),

            url: this.string(null).nullable(),
            src: this.string(null).nullable()
        };
    }

    async $upload(file, config = {}) {
        file = file || this.file_awaiting_upload;

        if (!file) {
            return;
        }

        if (!this.id) {
            const response = await this.constructor.api.post(this, config);
            this.id = response?.data?.created_id || response?.data?.id;
        }

        if (!this.send_file_url) {
            throw new Error('Media ID not found when trying to create file PUT URL');
        }

        await request({
            method: 'PUT',
            url: this.send_file_url,
            data: { file },
            params: config.params || {},
            headers: attachHeaders({ 'Content-Type': 'multipart/form-data' })
        });

        this.files_awaiting_upload = null;
    }

    async $uploadAudit(file, config = {}) {
        file = file || this.audit_file_awaiting_upload;

        if (!file) {
            return;
        }

        if (!this.id) {
            const response = await this.constructor.api.post(this, config);
            this.id = response?.data?.created_id || response?.data?.id;
        }

        if (!this.send_audit_file_url) {
            throw new Error('Media ID not found when trying to create audit file PUT URL');
        }

        await request({
            method: 'PUT',
            url: this.send_audit_file_url,
            data: { file },
            params: config.params || {},
            headers: attachHeaders({ 'Content-Type': 'multipart/form-data' })
        });

        this.audit_files_awaiting_upload = null;
    }

    async $getUrl(type = 'file') {
        if (type === 'file') {
            return await this.constructor.api.getUrl(this.id, this.file.id, 'inline');
        } else if (type === 'audit') {
            return await this.constructor.api.getUrl(this.id, this.audit_file.id, 'inline');
        }
    }

    async $download() {
        return await this.constructor.api.download(this.id, 'file');
    }

    async $downloadAudit() {
        return await this.constructor.api.download(this.id, 'audit');
    }

    get has_file() {
        return this.file && this.file.id;
    }

    get has_audit_file() {
        return this.audit_file && this.audit_file.id;
    }

    get is_type_adviser() {
        return this.library_type === MediaTypeEnum.ADVISER;
    }

    get is_type_investor() {
        return this.library_type === MediaTypeEnum.INVESTOR;
    }

    get is_type_marketing() {
        return this.library_type === MediaTypeEnum.MARKETING;
    }

    get base_url() {
        return `${config.API_BASE_URL}/v1/media/${this.library_key}`;
    }

    get send_file_url() {
        if (!this.id) {
            return null;
        }
        return `${this.base_url}/${this.id}/file`;
    }

    get send_audit_file_url() {
        if (!this.id) {
            return null;
        }
        return `${this.base_url}/${this.id}/audit`;
    }

    get library_key() {
        return kebabCase(this.library_type.toLowerCase());
    }

    get is_image() {
        return this.format === MediaFormatEnum.IMAGE;
    }

    get is_video() {
        return this.format === MediaFormatEnum.VIDEO;
    }

    get is_document() {
        return this.format === MediaFormatEnum.DOC;
    }

    get mimetype() {
        if (!this.file) {
            return null;
        }
        if (typeof this.file !== 'object') {
            return null;
        }

        if (!('mimetype' in this.file)) {
            return null;
        }

        return this.file.mimetype;
    }

    get icon() {
        let icon = getFileTypeIcon(this.mimetype);

        if (icon === 'file') {
            if (this.is_image) {
                return 'fileImage';
            } else if (this.is_video) {
                return 'fileVideo';
            } else if (this.is_document) {
                return 'fileDocument';
            }
        }

        return icon;
    }

    get file_name_format_options() {
        return {
            removeExtension: true
        };
    }

    get generated_file_name() {
        if (!this.file_awaiting_upload) {
            return null;
        }

        return formatString(this.file_awaiting_upload?.name, this.file_name_format_options);
    }

    get has_generated_name() {
        return this.name && this.name === this.generated_file_name;
    }

    get generated_file_description() {
        const name = this.generated_file_name;

        if (!name) {
            return null;
        }

        return toTipTap(name);
    }

    get has_generated_description() {
        return this.description && JSON.stringify(this.description) === JSON.stringify(this.generated_file_description);
    }

    get files_awaiting_upload() {
        return this.file_awaiting_upload ? [this.file_awaiting_upload] : [];
    }

    set files_awaiting_upload(files) {
        if (files && Array.isArray(files) && files.length) {
            const shouldChangeName = !this.name || this.has_generated_name;
            const shouldChangeDescription = !this.description || this.has_generated_description;

            this.file = null;
            this.file_awaiting_upload = files[0];

            if (shouldChangeName && files[0].name) {
                this.name = this.generated_file_name;
            }

            if (shouldChangeDescription && files[0].name) {
                this.description = this.generated_file_description;
            }

            if (files[0].type && files[0].type.startsWith('image/')) {
                this.format = MediaFormatEnum.IMAGE;
            } else if (files[0].type && files[0].type.startsWith('application/')) {
                this.format = MediaFormatEnum.DOC;
            }
        } else {
            if (this.has_generated_name) {
                this.name = null;
            }

            if (this.has_generated_description) {
                this.description = null;
            }

            this.file_awaiting_upload = null;
        }
    }

    get audit_files_awaiting_upload() {
        return this.audit_file_awaiting_upload ? [this.audit_file_awaiting_upload] : [];
    }

    set audit_files_awaiting_upload(files) {
        if (files && Array.isArray(files) && files.length) {
            this.audit_file = null;
            this.audit_file_awaiting_upload = files[0];
        } else {
            this.audit_file_awaiting_upload = null;
        }
    }

    get has_external_url() {
        return this.external_url;
    }

    get is_youtube_url() {
        return this.has_external_url && YouTubeUrl.validator(this.external_url) === true;
    }

    get is_valid_video() {
        return this.is_video && (this.has_file || this.has_external_url);
    }

    get file_status() {
        if (this.file && typeof this.file === 'object' && 'status' in this.file) {
            return this.file.status;
        }

        return null;
    }

    get file_being_scanned() {
        return this.file_status === 'IN_PROGRESS';
    }

    get file_clean() {
        return ['CLEAN', 'NOT_SCANNED'].includes(this.file_status);
    }

    get file_infected() {
        return this.file_status === 'INFECTED';
    }

    get file_scan_error() {
        return this.file_status === 'ERROR';
    }

    get audit_file_status() {
        if (this.audit_file && typeof this.audit_file === 'object' && 'status' in this.audit_file) {
            return this.audit_file.status;
        }

        return null;
    }

    get audit_file_being_scanned() {
        return this.audit_file_status === 'IN_PROGRESS';
    }

    get audit_file_clean() {
        return ['CLEAN', 'NOT_SCANNED'].includes(this.audit_file_status);
    }

    get audit_file_infected() {
        return this.audit_file_status === 'INFECTED';
    }

    get audit_file_scan_error() {
        return this.audit_file_status === 'ERROR';
    }

    get is_downloadable() {
        if (this.file_awaiting_upload) {
            return false;
        }

        if (this.has_external_url && !this.is_type_adviser) {
            return false;
        }

        if (this.file_being_scanned) {
            return false;
        }

        if (this.file_infected) {
            return false;
        }

        if (this.file_scan_error) {
            return false;
        }

        return this.has_file;
    }

    async toVideo() {
        if (!this.is_valid_video) {
            return null;
        }

        let options = {
            id: this.id,
            title: this.name,
            description: this.description,
            autoplay: false,
            muted: false,
            language: 'en',
            sources: []
            // poster: video.poster
        };

        if (this.is_youtube_url) {
            options.techOrder = ['youtube'];
            options.sources.push({
                type: 'video/youtube',
                src: this.external_url
            });
        } else if (this.has_file) {
            const url = await this.constructor.api.getFileUrl('file');
            options.sources.push({
                type: this.file.mimetype,
                src: url
            });
        }

        return options;
    }

    $clearAwaitingFiles() {
        this.src = null;
        this.file = null;
        this.file_awaiting_upload = null;
    }

    resetName() {
        this.name = this.generated_file_name;
    }
}

export default Media;
